import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDailySalesReport } from "../../redux/Slices/ReportsSlice";
import { FaChartLine, FaFileExcel, FaShoppingCart } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import PermissionButton from "../../components/PermissionButton";

import Pagination from '../../components/Pagination';
/* ── helpers ─────────────────────────────────────────────── */
const getAuthToken = () => {
  try {
    for (const store of [localStorage, sessionStorage]) {
      const raw = store.getItem("loggedUser") || store.getItem("user");
      if (raw) {
        const p = JSON.parse(raw);
        const t = p.token || p.data?.token || p.result?.token || p.user?.token;
        if (t) return t;
      }
    }
    return null;
  } catch { return null; }
};

const formatNum   = (n) => Number(n || 0).toLocaleString("ar-EG");
const payLabel    = (v) => ({ fawaterak: "فواتيرك", wallet: "المحفظة" }[v] ?? "نقدي");
const payIcon     = (v) => ({ fawaterak: "💳", wallet: "👛" }[v] ?? "💵");

const STATUS_MAP = {
  pending:     { label: "في الانتظار", bg: "#fef3c7", color: "#92400e" },
  in_progress: { label: "قيد التنفيذ",  bg: "#dbeafe", color: "#1e40af" },
  in_way:      { label: "في الطريق",    bg: "#ede9fe", color: "#5b21b6" },
  delivered:   { label: "تم التسليم",   bg: "#d1fae5", color: "#065f46" },
  cancelled:   { label: "ملغي",         bg: "#fee2e2", color: "#991b1b" },
};

const PAY_STATUS = {
  paid:     { label: "مدفوع",      bg: "#d1fae5", color: "#065f46" },
  not_paid: { label: "غير مدفوع", bg: "#fee2e2", color: "#991b1b" },
};

const Pill = ({ label, bg, color }) => (
  <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: bg, color, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
    {label}
  </span>
);

const getOrderStatusPill = (status, order) => {
  const isDigital =
    order?.is_digital || ["digital", "code"].includes(order?.type) ||
    ["digital", "code"].includes(order?.order_type) ||
    order?.category_name_en?.toLowerCase()?.includes("codes") ||
    order?.items?.some(i => i.product?.category_name_en?.toLowerCase()?.includes("codes"));
  if (isDigital) return <Pill label="طلب رقمي ✓" bg="#d1fae5" color="#065f46" />;
  const s = STATUS_MAP[status] || { label: status || "—", bg: "#f1f5f9", color: "#64748b" };
  return <Pill label={s.label} bg={s.bg} color={s.color} />;
};

const getPayStatusPill = (status) => {
  const s = PAY_STATUS[status] || { label: status || "—", bg: "#f1f5f9", color: "#64748b" };
  return <Pill label={s.label} bg={s.bg} color={s.color} />;
};

/* ── stat card ───────────────────────────────────────────── */
const StatCard = ({ icon, value, label, gradient, unit = "" }) => (
  <div style={{ background: gradient, borderRadius: 16, padding: "22px 20px", color: "#fff", boxShadow: "0 4px 20px rgba(0,0,0,.12)", position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", top: -16, right: -16, fontSize: 80, opacity: 0.12 }}>{icon}</div>
    <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>{value}{unit && <span style={{ fontSize: 16, fontWeight: 500, marginRight: 4 }}>{unit}</span>}</div>
    <div style={{ marginTop: 6, fontSize: 13, opacity: 0.9, fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 28, marginBottom: -4, marginTop: 6 }}>{icon}</div>
  </div>
);

/* ══════════════════════════════════════════════════════════
   Main
══════════════════════════════════════════════════════════ */
const DailySales = () => {
  const dispatch = useDispatch();
  const { dailySales, loading, error, meta } = useSelector((s) => s.reports);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo]     = useState(today);
  const [page, setPage]         = useState(1);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    dispatch(fetchDailySalesReport({ from: dateFrom, to: dateTo, page }));
  }, [dispatch, dateFrom, dateTo, page]);

  const handleDateFrom = (e) => { setDateFrom(e.target.value); setPage(1); };
  const handleDateTo   = (e) => { setDateTo(e.target.value);   setPage(1); };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const token = getAuthToken();
      if (!token) { toast.error("خطأ في المصادقة"); return; }
      const BASE = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";
      const res = await axios.get(`${BASE}/dashboard/reports/daily-sales/download`, {
        params: { from: dateFrom, to: dateTo },
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", `daily-sales-${dateFrom}-to-${dateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تحميل التقرير بنجاح");
    } catch { toast.error("حدث خطأ أثناء تحميل التقرير"); }
    finally  { setDownloading(false); }
  };

  const summary = dailySales?.summary || {};
  const orders  = dailySales?.orders  || [];

  /* ── Pagination ─────────────────────────────────────── */

  /* ══════════════════════════════════════════════════════
     Render
  ══════════════════════════════════════════════════════ */
  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Page header ────────────────────────────────── */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 22, display: "flex", alignItems: "center", gap: 8 }}>
            <FaChartLine style={{ color: "var(--main-color)" }} /> تقرير المبيعات اليومية
          </h4>
          <p style={{ margin: "3px 0 0", color: "#64748b", fontSize: 13 }}>
            الفترة من <strong>{dateFrom}</strong> إلى <strong>{dateTo}</strong>
          </p>
        </div>
        <PermissionButton
          permission="view-sales-reports"
          onClick={handleDownload}
          disabled={downloading}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 22px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,var(--green-color),#059669)", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          {downloading
            ? <><span className="spinner-border spinner-border-sm"></span> جاري التحميل...</>
            : <><FaFileExcel /> تصدير Excel</>}
        </PermissionButton>
      </div>

      {/* ── Date filter ────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "18px 22px", marginBottom: 24, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>من تاريخ</label>
          <input type="date" value={dateFrom} onChange={handleDateFrom}
            style={{ padding: "9px 14px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", background: "#f8fafc", cursor: "pointer" }}
            onFocus={(e) => (e.target.style.borderColor = "var(--main-color)")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>إلى تاريخ</label>
          <input type="date" value={dateTo} onChange={handleDateTo}
            style={{ padding: "9px 14px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", background: "#f8fafc", cursor: "pointer" }}
            onFocus={(e) => (e.target.style.borderColor = "var(--main-color)")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")} />
        </div>
        <button
          onClick={() => { setDateFrom(today); setDateTo(today); setPage(1); }}
          style={{ padding: "9px 18px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#f1f5f9", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
          اليوم فقط
        </button>
      </div>

      {/* ── Loading ─────────────────────────────────────── */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[140, 100, 300].map((h, i) => (
            <div key={i} style={{ height: h, borderRadius: 14, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Error ───────────────────────────────────────── */}
      {!loading && error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 20, color: "var(--red-color)", fontWeight: 600 }}>⚠ {error}</div>
      )}

      {/* ── Content ─────────────────────────────────────── */}
      {!loading && !error && (
        <>
          {/* ── KPI cards ─────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
            <StatCard icon="🛒" value={formatNum(summary.orders_count)} label="الطلبات المدفوعة"    gradient="linear-gradient(135deg,var(--main-color),var(--main-light))" />
            <StatCard icon="💰" value={formatNum(summary.total_sales)}  label="إجمالي المبيعات (صافي)" gradient="linear-gradient(135deg,var(--green-color),#34d399)" unit="ج.م" />
            <StatCard icon="📦" value={formatNum(summary.items_sold)}   label="المنتجات المباعة"    gradient="linear-gradient(135deg,#0ea5e9,#38bdf8)" />
            <StatCard icon="📊" value={formatNum(summary.average_order_value)} label="متوسط قيمة الطلب" gradient="linear-gradient(135deg,#f59e0b,var(--warning-color))" unit="ج.م" />
          </div>

          {/* ── Revenue & Payment breakdown ─────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16, marginBottom: 28 }}>
            {/* Revenue */}
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", fontWeight: 700, color: "#0f172a", fontSize: 15 }}>💹 تفاصيل الإيرادات</div>
              {[
                { label: "إجمالي المبيعات قبل الخصم", value: summary.gross_sales,       plus: true  },
                { label: "خصومات الكوبونات",           value: summary.coupon_discounts,  minus: true },
                { label: "رسوم التوصيل",               value: summary.shipping_fees,     ship: true  },
              ].map((row, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: "1px solid #f8fafc" }}>
                  <span style={{ color: "#64748b", fontSize: 14 }}>{row.label}</span>
                  <strong style={{ color: row.minus ? "var(--red-color)" : row.ship ? "#0ea5e9" : "var(--green-color)", fontSize: 15 }}>
                    {row.minus ? "−" : "+"} {formatNum(row.value)} <span style={{ fontSize: 11, fontWeight: 500 }}>ج.م</span>
                  </strong>
                </div>
              ))}
            </div>

            {/* Payment methods */}
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", fontWeight: 700, color: "#0f172a", fontSize: 15 }}>💳 طرق الدفع</div>
              {summary.by_payment_provider?.length > 0 ? (
                summary.by_payment_provider.map((p, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: "1px solid #f8fafc" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 20 }}>{payIcon(p.payment_provider)}</span>
                      <span style={{ color: "#334155", fontSize: 14 }}>
                        {payLabel(p.payment_provider)}
                        <span style={{ fontSize: 11, color: "#94a3b8", marginRight: 6 }}>({p.orders_count} طلب)</span>
                      </span>
                    </div>
                    <strong style={{ color: "#0f172a" }}>{formatNum(p.total_sales)} <span style={{ fontSize: 11, fontWeight: 500 }}>ج.م</span></strong>
                  </div>
                ))
              ) : (
                <div style={{ padding: "30px 20px", textAlign: "center", color: "#94a3b8" }}>لا توجد مدفوعات</div>
              )}
            </div>
          </div>

          {/* ── Orders section ─────────────────────────── */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <FaShoppingCart style={{ color: "var(--main-color)", fontSize: 18 }} />
              <h5 style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: 17 }}>
                الطلبات المدفوعة
                {meta?.total != null && (
                  <span style={{ marginRight: 8, fontSize: 13, fontWeight: 500, color: "#64748b" }}>({formatNum(meta.total)} طلب)</span>
                )}
              </h5>
            </div>

            {orders.length === 0 ? (
              <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "60px 20px", textAlign: "center", color: "#94a3b8" }}>
                <div style={{ fontSize: 52 }}>🛒</div>
                <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15 }}>لا توجد طلبات مدفوعة في هذه الفترة</p>
              </div>
            ) : (
              <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        {["رقم الطلب", "نوع", "العميل", "المنتجات", "الدفع", "المجموع", "حالة الدفع", "حالة الطلب"].map((h, i) => (
                          <th key={i} style={{ padding: "12px 14px", textAlign: "center", fontWeight: 700, color: "#475569", fontSize: 12, borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, idx) => (
                        <tr key={order.id}
                          style={{ borderBottom: "1px solid #f1f5f9", background: idx % 2 === 0 ? "#fff" : "#fafafa" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f4ff")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafafa")}
                        >
                          <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 700, color: "var(--main-color)" }}>{order.order_number}</td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>
                            {["code","digital"].includes(order.order_type) || ["code","digital"].includes(order.type)
                              ? <Pill label="🔑 كود"  bg="#ede9fe" color="#5b21b6" />
                              : ["book"].includes(order.order_type) || ["book"].includes(order.type)
                              ? <Pill label="📚 كتاب" bg="#fef3c7" color="#92400e" />
                              : <Pill label="—"       bg="#f1f5f9" color="#64748b" />}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center", color: "#334155", fontWeight: 500 }}>
                            {order.user_name || order.user?.name || "—"}
                          </td>
                          <td style={{ padding: "10px 14px", minWidth: 200 }}>
                            {(order.items?.length > 0 ? order.items : order.products?.length > 0 ? order.products : []).length > 0 ? (
                              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                                {(order.items || order.products || []).map((item, i2) => {
                                  const name  = item.product_name || item.product?.title_ar || item.title_ar || item.title_en || "منتج";
                                  const qty   = item.quantity || item.qty || item.pivot?.quantity || 1;
                                  const price = item.price || item.product?.price || 0;
                                  return (
                                    <li key={i2} style={{ padding: "2px 0", color: "#475569" }}>
                                      • {name} <span style={{ color: "#94a3b8" }}>×{qty}</span>
                                      <span style={{ color: "var(--green-color)", marginRight: 4 }}> {formatNum(price)} ج.م</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : <span style={{ color: "#cbd5e1" }}>— لا تفاصيل —</span>}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>
                            <span style={{ fontSize: 16 }}>{payIcon(order.payment_provider)}</span>
                            <div style={{ fontSize: 11, color: "#64748b" }}>{payLabel(order.payment_provider)}</div>
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>
                            <strong style={{ color: "var(--green-color)", fontSize: 15 }}>{formatNum(order.total_after_discount || order.grand_total)} ج.م</strong>
                            {order.total_before_discount && order.total_before_discount !== order.total_after_discount && (
                              <div style={{ color: "#94a3b8", textDecoration: "line-through", fontSize: 11 }}>{formatNum(order.total_before_discount)} ج.م</div>
                            )}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>{getPayStatusPill(order.payment_status)}</td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>{getOrderStatusPill(order.order_status || order.status, order)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination meta={meta} currentPage={page} loading={loading} onChange={setPage} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DailySales;
